import React, { useEffect, useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTASection from '@/components/CTASection';
import Button from '@/components/Button';
import { projects } from '@/data/projects';
import type { Project } from '@/types';
import styles from './ProjectDetails.module.scss';

const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);

  useEffect(() => {
    const foundProject = projects.find((p) => p.id === id);
    if (foundProject) {
      setProject(foundProject);
    }
  }, [id]);

  if (!project) {
    return (
      <IonPage id="project-details-page">
        <IonContent fullscreen scrollY scrollEvents={true}>
          <div className="page-wrapper">
            <Navbar />
            <div style={{ padding: '160px 24px', textAlign: 'center' }}>
              <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Project not found</h2>
              <Link to="/work" style={{ color: 'var(--color-orange, #FF7A00)', textDecoration: 'underline' }}>
                Back to Work
              </Link>
            </div>
            <Footer />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const currentIndex = projects.findIndex((p) => p.id === project.id);
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : projects[projects.length - 1];
  const nextProject = currentIndex >= 0 && currentIndex < projects.length - 1 ? projects[currentIndex + 1] : projects[0];

  const prevUrl = prevProject ? `/work/${prevProject.id}` : '/work';
  const nextUrl = nextProject ? `/work/${nextProject.id}` : '/work';

  return (
    <IonPage id="project-details-page">
      <IonContent fullscreen scrollY scrollEvents={true}>
        <div className="page-wrapper">
          {/* 1. ARKATECHS NAVBAR */}
          <Navbar />

          {/* 2. PROJECT HERO (Clean, Minimal Project Introduction) */}
          <section className={`${styles.hero} dark-section`} id="project-hero">
            <div className={`container ${styles.heroContainer}`}>
              <div className={styles.heroHeader}>
                {/* LEFT: Project Name, category, description */}
                <motion.div
                  className={styles.heroIntro}
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <span className={styles.eyebrow}>{project.category}</span>
                  <h1 className={styles.heroTitle}>{project.title}</h1>
                  <p className={styles.heroSub}>{project.description}</p>
                </motion.div>

                {/* RIGHT: Small project metadata */}
                <motion.div
                  className={styles.heroMetaRight}
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <div className={styles.heroMetaItem}>
                    <span className={styles.heroMetaLabel}>CLIENT</span>
                    <span className={styles.heroMetaValue}>{project.client || project.title}</span>
                  </div>
                  {project.role && (
                    <div className={styles.heroMetaItem}>
                      <span className={styles.heroMetaLabel}>ROLE</span>
                      <span className={styles.heroMetaValue}>{project.role}</span>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* 3. HERO MOCKUP (First supplied image, complete and uncropped) */}
              <motion.div
                className={styles.mockupSection}
                initial={{ opacity: 0, y: 35 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className={styles.mockupFrame}>
                  <img
                    src={project.image}
                    alt={`${project.title} Main Mockup`}
                    className={styles.mockupImage}
                  />
                </div>
              </motion.div>
            </div>

            {/* Signature Arkatechs transition to Light Section */}
            <div className="torn-edge-bottom" />
          </section>

          {/* 4. PROJECT META + OVERVIEW (Light Section) */}
          <section className={`${styles.infoSection} light-section`} id="project-info">
            <div className="container">
              <div className={styles.infoGrid}>
                {/* LEFT COLUMN: Metadata */}
                <motion.div
                  className={styles.infoLeft}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className={styles.metaRow}>
                    <span className={styles.metaLabel}>PROJECT</span>
                    <span className={styles.metaValue}>{project.title}</span>
                  </div>

                  {project.industry && (
                    <div className={styles.metaRow}>
                      <span className={styles.metaLabel}>INDUSTRY</span>
                      <span className={styles.metaValue}>{project.industry}</span>
                    </div>
                  )}

                  <div className={styles.metaRow}>
                    <span className={styles.metaLabel}>TYPE</span>
                    <span className={styles.metaValue}>{project.category}</span>
                  </div>

                  {project.role && (
                    <div className={styles.metaRow}>
                      <span className={styles.metaLabel}>ROLE</span>
                      <span className={styles.metaValue}>{project.role}</span>
                    </div>
                  )}
                </motion.div>

                {/* RIGHT COLUMN: Overview & Live Website Button */}
                <motion.div
                  className={styles.infoRight}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                >
                  <h2 className={styles.infoHeading}>PROJECT OVERVIEW</h2>
                  <p className={styles.infoText}>
                    {project.longDescription || project.description}
                  </p>

                  {/* 5. LIVE WEBSITE & PLAY STORE BUTTONS */}
                  {(project.liveUrl || project.playStoreUrl) && (
                    <div className={styles.liveWebsiteWrapper}>
                      {project.liveUrl && (
                        <Button
                          href={project.liveUrl}
                          variant="primary"
                          icon={<ArrowUpRight size={16} />}
                          iconPosition="right"
                        >
                          Visit Live Website
                        </Button>
                      )}
                      {project.playStoreUrl && (
                        <Button
                          href={project.playStoreUrl}
                          variant="secondary"
                          icon={<ArrowUpRight size={16} />}
                          iconPosition="right"
                        >
                          Get on Google Play
                        </Button>
                      )}
                    </div>
                  )}
                </motion.div>
              </div>

              {/* 6. PROJECT SHOWCASE (Exact 3 showcase images) */}
              {project.gallery && project.gallery.length > 0 && (
                <div className={styles.showcaseWrapper}>
                  <motion.div
                    className={styles.sectionHeadingWrapper}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className={styles.sectionEyebrow}>DIGITAL EXPERIENCE</span>
                    <h2 className={styles.sectionTitle}>PROJECT SHOWCASE</h2>
                    <div className="gradient-line" />
                  </motion.div>

                  {/* Showcase Image 1: Large visual */}
                  {project.gallery[0] && (
                    <motion.div
                      className={styles.largeScreenshotFrame}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7 }}
                    >
                      <img
                        src={project.gallery[0]}
                        alt={`${project.title} Showcase 1`}
                        className={styles.showcaseImage}
                      />
                    </motion.div>
                  )}

                  {/* Showcase Images 2 & 3: Two-column visual grid */}
                  {project.gallery.length > 1 && (
                    <div className={styles.supportingGrid}>
                      {project.gallery.slice(1, 3).map((imgUrl, i) => (
                        <motion.div
                          key={i}
                          className={styles.supportingFrame}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.7, delay: (i + 1) * 0.1 }}
                        >
                          <img
                            src={imgUrl}
                            alt={`${project.title} Showcase ${i + 2}`}
                            className={styles.showcaseImage}
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 7. WHAT WE BUILT */}
              {project.whatWeBuilt && project.whatWeBuilt.length > 0 && (
                <div className={styles.builtWrapper}>
                  <motion.div
                    className={styles.sectionHeadingWrapper}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <span className={styles.sectionEyebrow}>SCOPE & DELIVERABLES</span>
                    <h2 className={styles.sectionTitle}>WHAT WE BUILT</h2>
                    <div className="gradient-line" />
                  </motion.div>

                  <div className={styles.builtGrid}>
                    {project.whatWeBuilt.map((feature, index) => {
                      const num = String(index + 1).padStart(2, '0');
                      const title = typeof feature === 'string' ? feature : (feature as { title: string; desc?: string }).title;
                      const desc = typeof feature === 'string' ? '' : (feature as { title: string; desc?: string }).desc;
                      return (
                        <motion.div
                          key={num}
                          className={styles.builtCard}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.05 }}
                        >
                          <div className={styles.cardHeader}>
                            <span className={styles.cardNumber}>{num}</span>
                            <CheckCircle2 size={16} className={styles.cardCheck} />
                          </div>
                          <h3 className={styles.cardTitle}>{title}</h3>
                          {desc && <p className={styles.cardDesc}>{desc}</p>}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 8. PROJECT NAVIGATION */}
              <motion.div
                className={styles.navWrapper}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className={styles.navInner}>
                  <Link to={prevUrl} className={styles.navLink}>
                    <ArrowLeft size={16} />
                    <span>PREVIOUS PROJECT</span>
                  </Link>

                  <Link to="/work" className={styles.navLinkCenter}>
                    BACK TO WORK
                  </Link>

                  <Link to={nextUrl} className={styles.navLink}>
                    <span>NEXT PROJECT</span>
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>

          {/* 9. EXISTING ARKATECHS CTA */}
          <CTASection
            title="HAVE AN IDEA WORTH BUILDING?"
            primaryLabel="LET'S MAKE IT REAL."
            primaryTo="/contact"
          />

          {/* 10. EXISTING ARKATECHS FOOTER */}
          <Footer />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ProjectDetails;
